import React, { useState } from 'react';
import { useEffect } from 'react';
import { Search, Filter, Phone, MessageCircle, Mail, Globe, Linkedin, Download, Users, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { useGoogleSheets } from '../../hooks/useGoogleSheets';
import StatsCard from '../Dashboard/StatsCard';
import { WEBHOOK_URLS } from '../../shared/constants';

const QualifiedReadyLeadsPage: React.FC = () => {
  const { data: qualifiedLeads, isLoading, error, isAuthenticated, updateRecord, updateMultipleRecords } = useGoogleSheets('qualified');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLeadIds, setSelectedLeadIds] = useState<number[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const filteredLeads = qualifiedLeads.filter(lead => {
    const matchesSearch = (lead.company_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (lead.dm_full_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (lead.dm_email_1 || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (lead.industry_1 || '').toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const totalPages = Math.ceil(filteredLeads.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedLeads = filteredLeads.slice(startIndex, startIndex + itemsPerPage);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);


  // Calculate statistics
  const totalQualifiedLeads = qualifiedLeads.length;
  const leadsReadyForOutreach = totalQualifiedLeads;
  const leadsWithMobileNumbers = qualifiedLeads.filter(lead => 
    lead.dm_mobile_1 && lead.dm_mobile_1.trim() !== ''
  ).length;
  const leadsWithEmailAddresses = qualifiedLeads.filter(lead => 
    lead.dm_email_1 && lead.dm_email_1.trim() !== ''
  ).length;

  const handleSelectAll = () => {
    const allFilteredIds = paginatedLeads.map(lead => lead._rowIndex).filter(Boolean);
    if (selectedLeadIds.length === allFilteredIds.length && allFilteredIds.length > 0) {
      setSelectedLeadIds([]);
    } else {
      setSelectedLeadIds(allFilteredIds);
    }
  };

  const handleSelectLead = (rowIndex: number) => {
    if (!rowIndex) return;
    
    setSelectedLeadIds(prev => {
      if (prev.includes(rowIndex)) {
        return prev.filter(id => id !== rowIndex);
      } else {
        return [...prev, rowIndex];
      }
    });
  };

  const handleBulkAction = async (action: string) => {
    if (selectedLeadIds.length === 0) return;
    
    try {
      await updateMultipleRecords(selectedLeadIds, 'send_for_outreach', action);
      setSelectedLeadIds([]); // Clear selection after action
    } catch (error) {
      console.error('Failed to perform bulk action:', error);
    }
  };

  const handleExportCSV = () => {
    if (selectedLeadIds.length === 0) return;
    
    const selectedLeads = filteredLeads.filter(lead => selectedLeadIds.includes(lead._rowIndex));
    
    // CSV headers
    const csvHeaders = [
      'Company Name', 'Country', 'Company Phone', 'Company Website', 'Company LinkedIn',
      'Industry', 'Products/Services', 'Person Name', 'Person Title', 'Person Mobile', 
      'Person Email', 'Person LinkedIn', 'Lead Status'
    ];
    
    // CSV rows
    const csvRows = selectedLeads.map(lead => [
      lead.company_name || '',
      lead.country || '',
      lead.company_phone || '',
      lead.company_website || '',
      lead.company_linkedin || '',
      lead.industry_1 || '',
      lead.products_services || '',
      lead.dm_full_name || '',
      lead.dm_title || '',
      lead.dm_mobile_1 || '',
      lead.dm_email_1 || '',
      lead.dm_linkedin || '',
      lead.lead_status || ''
    ]);
    
    // Create CSV content
    const csvContent = [csvHeaders, ...csvRows]
      .map(row => row.map(field => `"${field.toString().replace(/"/g, '""')}"`).join(','))
      .join('\n');
    
    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `qualified-leads-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleActionChange = async (lead: any, action: string) => {
    if (!action || !lead._rowIndex) return;
    
    try {
      // Send webhook if action is Call, Email, or WhatsApp
      if (action === 'Call' || action === 'Email' || action === 'WhatsApp') {
        const webhookUrl = WEBHOOK_URLS[action as keyof typeof WEBHOOK_URLS];
        
        const webhookPayload = {
          action: action,
          lead_id: lead.lead_id || '',
          dm_full_name: lead.dm_full_name || '',
          dm_mobile_1: lead.dm_mobile_1 || '',
          dm_mobile_2: lead.dm_mobile_2 || '',
          dm_email_1: lead.dm_email_1 || '',
          dm_email_2: lead.dm_email_2 || '',
          dm_linkedin: lead.dm_linkedin || '',
          dm_title: lead.dm_title || '',
          company_name: lead.company_name || '',
          company_website: lead.company_website || '',
          company_linkedin: lead.company_linkedin || '',
          country: lead.country || '',
          employee_headcount: lead.employee_headcount || '',
          industry_1: lead.industry_1 || '',
          industry_2: lead.industry_2 || '',
          products_services: lead.products_services || '',
          company_type: lead.company_type || ''
        };

        console.log(`[QualifiedReadyLeadsPage] Sending ${action} webhook:`, webhookPayload);
        
        try {
          await fetch(webhookUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(webhookPayload)
          });
          console.log(`[QualifiedReadyLeadsPage] ${action} webhook sent successfully`);
        } catch (webhookError) {
          console.error(`[QualifiedReadyLeadsPage] Failed to send ${action} webhook:`, webhookError);
          // Don't fail the entire operation if webhook fails
        }
      }
      
      // Update the Google Sheet
      await updateRecord(lead._rowIndex, 'send_for_outreach', action);
    } catch (error) {
      console.error('Failed to update action:', error);
    }
  };

  // Only show loading if we're loading AND have no data to display
  if (isLoading && qualifiedLeads.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <div className="text-gray-500 text-lg mb-2">Loading data...</div>
          <div className="text-gray-400 text-sm">Please Make Sure The System Is Connected</div>
        </div>
      </div>
    );
  }

  const getLeadStatusTag = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'ready to go':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-400">
            Ready to Go 🚀
          </span>
        );
      case 'looks good':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
            Looks Good 👌
          </span>
        );
      case 'reach via linkedin':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200">
            Reach via LinkedIn 🧑‍💼
          </span>
        );
      case 'needs enrichment':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200">
            Needs Enrichment 🧩
          </span>
        );
      case 'not a fit':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">
            Not a Fit ❌
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200">
            {status || 'Unknown'}
          </span>
        );
    }
  };

  const getAvailableActions = (lead: any) => {
    const actions = [];
    
    if (lead.dm_mobile_1) {
      actions.push('Call', 'WhatsApp');
    }
    if (lead.dm_email_1) {
      actions.push('Email');
    }
    if (lead.dm_linkedin) {
      actions.push('LinkedIn');
    }
    
    return actions;
  };

  return (
    <div className="space-y-6">
      {/* Connection Status Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 p-3 rounded-lg flex items-center space-x-2 mb-6">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard
          title="Total Qualified Leads"
          value={totalQualifiedLeads}
          icon={Users}
          color="blue"
        />
        <StatsCard
          title="Ready for Outreach"
          value={leadsReadyForOutreach}
          icon={CheckCircle}
          color="green"
        />
        <StatsCard
          title="Leads with Mobile"
          value={leadsWithMobileNumbers}
          icon={Phone}
          color="purple"
        />
        <StatsCard
          title="Leads with Email"
          value={leadsWithEmailAddresses}
          icon={Mail}
          color="orange"
        />
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search qualified leads..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedLeadIds.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <CheckCircle className="text-blue-600" size={20} />
              <span className="text-blue-800 font-medium">
                {selectedLeadIds.length} lead{selectedLeadIds.length > 1 ? 's' : ''} selected
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleBulkAction('Call')}
                className="flex items-center space-x-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Phone size={16} />
                <span>Send To Call Agent</span>
              </button>
              <button
                onClick={() => handleBulkAction('WhatsApp')}
                className="flex items-center space-x-1 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <MessageCircle size={16} />
                <span>Send To WhatsApp Agent</span>
              </button>
              <button
                onClick={() => handleBulkAction('Email')}
                className="flex items-center space-x-1 px-3 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
              >
                <Mail size={16} />
                <span>Send To Email Agent</span>
              </button>
              <button
                onClick={() => handleBulkAction('Do all')}
                className="flex items-center space-x-1 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                <span>⚡</span>
                <span>Do All</span>
              </button>
              <button
                onClick={handleExportCSV}
                className="flex items-center space-x-1 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Download size={16} />
                <span>Export CSV</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-max">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 w-12">
                  <input
                    type="checkbox"
                    checked={paginatedLeads.length > 0 && selectedLeadIds.length === paginatedLeads.map(lead => lead._rowIndex).filter(Boolean).length}
                    ref={(el) => {
                      if (el) {
                        const allFilteredIds = paginatedLeads.map(lead => lead._rowIndex).filter(Boolean);
                        el.indeterminate = selectedLeadIds.length > 0 && selectedLeadIds.length < allFilteredIds.length;
                      }
                    }}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-48">
                  Company Name
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-44">
                  Industry & Products
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-44">
                  Person
                </th>
                <th className="text-left px-2 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                  Lead Status
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-48">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedLeads.map((lead) => (
                <tr key={lead._rowIndex} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-4">
                    <input
                      type="checkbox"
                      checked={lead._rowIndex ? selectedLeadIds.includes(lead._rowIndex) : false}
                      onChange={() => handleSelectLead(lead._rowIndex)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </td>
                  <td className="px-4 py-4">
                    <div className="font-medium text-gray-900 break-words">{lead.company_name || ''}</div>
                    <div className="text-sm text-gray-500 break-words">{lead.country || ''}</div>
                    <div className="text-sm text-gray-500 break-words">
                      {lead.company_phone && lead.company_phone.trim() ? (
                        lead.company_phone
                      ) : (
                        <span className="text-gray-400 italic flex items-center space-x-1">
                          <Search size={12} className="animate-pulse" />
                          <span>Searching...</span>
                        </span>
                      )}
                    </div>
                    <div className="flex items-center space-x-2 mt-2">
                      {lead.company_website && (
                        <a 
                          href={lead.company_website.startsWith('http') ? lead.company_website : `https://${lead.company_website}`}
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 transition-colors"
                          title="Visit Website"
                        >
                          <Globe size={16} />
                        </a>
                      )}
                      {lead.company_linkedin && (
                        <a 
                          href={lead.company_linkedin.startsWith('http') ? lead.company_linkedin : `https://${lead.company_linkedin}`}
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 transition-colors"
                          title="LinkedIn Profile"
                        >
                          <Linkedin size={16} />
                        </a>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="text-sm text-gray-900 break-words">
                      {lead.industry_1 && lead.industry_1.trim() ? (
                        lead.industry_1
                      ) : (
                        <span className="text-gray-400 italic flex items-center space-x-1">
                          <Loader2 size={12} className="animate-spin" />
                          <span>Analyzing...</span>
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-500 mt-1 break-words">
                      {lead.products_services && lead.products_services.trim() ? (
                        lead.products_services
                      ) : (
                        <span className="text-gray-400 italic flex items-center space-x-1">
                          <Loader2 size={12} className="animate-spin" />
                          <span>Analyzing...</span>
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4 w-44">
                    <div className="flex items-center space-x-2">
                      <div className="font-medium text-gray-900 break-words">{lead.dm_full_name || ''}</div>
                      {lead.dm_linkedin && (
                        <a 
                          href={lead.dm_linkedin.startsWith('http') ? lead.dm_linkedin : `https://${lead.dm_linkedin}`}
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 transition-colors"
                          title="LinkedIn Profile"
                        >
                          <Linkedin size={14} />
                        </a>
                      )}
                    </div>
                    <div className="text-sm text-gray-500 break-words">{lead.dm_title || ''}</div>
                    <div className="text-sm text-gray-500 break-words">
                      {lead.dm_mobile_1 && lead.dm_mobile_1.trim() ? (
                        lead.dm_mobile_1
                      ) : (
                        <span className="text-gray-400 italic flex items-center space-x-1">
                          <Search size={12} className="animate-pulse" />
                          <span>Searching...</span>
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-500 break-words">
                      {lead.dm_email_1 && lead.dm_email_1.trim() ? (
                        lead.dm_email_1
                      ) : (
                        <span className="text-gray-400 italic flex items-center space-x-1">
                          <Search size={12} className="animate-pulse" />
                          <span>Searching...</span>
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-4 text-left w-32">
                    {getLeadStatusTag(lead.lead_status)}
                  </td>
                  <td className="px-4 py-4 w-48">
                    <div className="flex flex-col space-y-2">
                      {/* Current Action Display */}
                      {lead.send_for_outreach && (
                        <div className="flex items-center">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                            {lead.send_for_outreach === 'Call' && '📞 Call'}
                            {lead.send_for_outreach === 'WhatsApp' && '💬 WhatsApp'}
                            {lead.send_for_outreach === 'Email' && '📧 Email'}
                            {lead.send_for_outreach === 'LinkedIn' && '🔗 LinkedIn'}
                            {lead.send_for_outreach === 'Do all' && '⚡ Do All'}
                            {lead.send_for_outreach === 'do all' && '⚡ Do All'}
                            {!['Call', 'WhatsApp', 'Email', 'LinkedIn', 'Do all', 'do all'].includes(lead.send_for_outreach) && lead.send_for_outreach}
                          </span>
                        </div>
                      )}
                      {/* Action Dropdown */}
                      <select
                        value={lead.send_for_outreach || ''}
                        onChange={(e) => {
                          if (e.target.value) {
                            handleActionChange(lead, e.target.value);
                          }
                        }}
                        className="w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-400 transition-colors appearance-none cursor-pointer"
                        style={{
                          backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                          backgroundPosition: 'right 0.5rem center',
                          backgroundRepeat: 'no-repeat',
                          backgroundSize: '1.5em 1.5em',
                          paddingRight: '2.5rem'
                        }}
                      >
                        <option value="">Select Action</option>
                        {lead.dm_mobile_1 && (
                          <>
                            <option value="Call">📞 Call</option>
                            <option value="WhatsApp">💬 WhatsApp</option>
                          </>
                        )}
                        {lead.dm_email_1 && <option value="Email">📧 Email</option>}
                        {lead.dm_linkedin && <option value="LinkedIn">🔗 LinkedIn</option>}
                        <option value="Do all">⚡ Do All</option>
                      </select>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="bg-white rounded-xl border border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredLeads.length)} of {filteredLeads.length} results
          </div>
          {totalPages > 1 && (
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="px-3 py-2 text-sm text-gray-700">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>

      {paginatedLeads.length === 0 && !isLoading && (
        <div className="text-center py-12">
          <div className="text-gray-500 text-lg">No qualified leads found</div>
          <div className="text-gray-400 text-sm mt-1">
            {searchTerm ? 'Try adjusting your search' : 'Qualified leads will appear here once they are processed'}
          </div>
        </div>
      )}
    </div>
  );
};

export default QualifiedReadyLeadsPage;